import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import logoImage from '../assets/so_coffee_logo.png';

const ENVIRONMENT = import.meta.env.APP_ENV;
const BASE_URL =
  ENVIRONMENT === 'production'
    ? 'https://socoffee.mooo.com/api-server/DryingBedsMaster/v1.0/get-drying-bed-info-by-scan'
    : 'https://socoffeedev.orderstack.co/api-server/DryingBedsMaster/v1.0/get-drying-bed-info-by-scan';

let logoDataUrl = null;

async function loadLogoDataUrl() {
  if (logoDataUrl) return logoDataUrl;

  try {
    const response = await fetch(logoImage);
    const blob = await response.blob();
    logoDataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    return logoDataUrl;
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
}

async function getImageDimensions(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = dataUrl;
  });
}

// Load logo once when module loads
const LOGO_DATA_URL_PROMISE = loadLogoDataUrl();

function formatDate(dateValue) {
  if (!dateValue) return 'N/A';

  const date = new Date(dateValue);

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return 'N/A';
  }

  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function drawLabelValue(doc, label, value, x, y, valueOffset = 37) {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);

  doc.setFont('helvetica', 'normal');
  doc.text(value, x + valueOffset, y);
}

export async function generateBedPdf({
  lot_name,
  estate_name,
  harvest_date,
  drying_start_date,
  active_beds,
  bed_number,
  bed_position,
  bed_label,
}) {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a5',
  });

  // ---- QR DATA ----
  const qrValue = `${BASE_URL}?bed_number=${bed_number}&position=${bed_position.toUpperCase()}&redirect=true`;
  //   const qrValue = `${BASE_URL}?bed_number=${bed}&position=${position}&redirect=true`;

  const qrDataUrl = await QRCode.toDataURL(qrValue, {
    margin: 1,
    width: 300,
  });

  // ---- QR IMAGE ----
  doc.addImage(qrDataUrl, 'PNG', 5, 10, 80, 80);

  // ---- TEXT CONTENT ----
  let x = 95;
  let y = 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(lot_name, x, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(16);

  // Estate
  y += 10;
  drawLabelValue(doc, 'Estate:', estate_name, x, y);

  // Harvest Date
  y += 10;
  drawLabelValue(doc, 'Harvest Date:', formatDate(harvest_date), x, y);
  // drawLabelValue(
  //   doc,
  //   'Harvest Date:',
  //   new Date(harvest_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
  //   x,
  //   y
  // );

  // Drying start
  y += 10;
  drawLabelValue(doc, 'Drying start:', formatDate(drying_start_date), x, y);

  // Active beds
  y += 10;

  const label = `Active beds (${active_beds.length - 1} beds):`;
  const value = active_beds.filter(b => b !== bed_label).join(', ');

  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);

  const labelWidth = doc.getTextWidth(label);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const rightMargin = 10;
  const lineHeight = 6;

  // Width available AFTER label
  const firstLineWidth = pageWidth - (x + labelWidth + 2) - rightMargin;

  // Width for lines below (full width from x)
  const nextLineWidth = pageWidth - x - rightMargin;

  // Split text
  doc.setFont('helvetica', 'normal');

  const firstLine = doc.splitTextToSize(value, firstLineWidth);
  const remainingText = firstLine.slice(1).join(' ');

  doc.text(firstLine[0], x + labelWidth + 2, y);

  // Wrap remaining lines properly
  const remainingLines = doc.splitTextToSize(remainingText, nextLineWidth);

  for (const line of remainingLines) {
    y += lineHeight;

    // 🔴 Page overflow check
    if (y > pageHeight - 10) {
      doc.addPage();
      y = 20;
    }

    doc.text(line, x, y);
  }

  // Footer
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(bed_label, 45, 100);

  const logoDataUrl = await LOGO_DATA_URL_PROMISE;

  if (logoDataUrl) {
    const logoDimensions = await getImageDimensions(logoDataUrl);
    const aspectRatio = logoDimensions.width / logoDimensions.height;

    const logoWidth = 45;
    const logoHeight = logoWidth / aspectRatio;
    doc.addImage(logoDataUrl, 'PNG', 155, 75, logoWidth, logoHeight);
  }

  // Save
  doc.save(`bed-${bed_label}.pdf`);
}

function getBedLabel(bed_number, bed_position) {
  const map = {
    upper: 'U',
    middle: 'M',
    lower: 'L',
  };

  return map[bed_position] + String(bed_number).padStart(2, '0');
}

function getActiveBedsSorted(data) {
  const order = { upper: 1, middle: 2, lower: 3 };
  const prefix = { upper: 'U', middle: 'M', lower: 'L' };

  return data
    .filter(b => b.drying_status === true)
    .sort((a, b) => {
      if (order[a.bed_position] !== order[b.bed_position]) {
        return order[a.bed_position] - order[b.bed_position];
      }
      return a.bed_number - b.bed_number;
    })
    .map(b => prefix[b.bed_position] + String(b.bed_number).padStart(2, '0'));
}

async function renderBedPage(
  doc,
  { lot_name, estate_name, harvest_date, drying_start_date, active_beds, bed_number, bed_position, bed_label }
) {
  // ---- QR DATA ----
  const qrValue = `${BASE_URL}?bed_number=${bed_number}&position=${bed_position.toUpperCase()}&redirect=true`;

  const qrDataUrl = await QRCode.toDataURL(qrValue, {
    margin: 1,
    width: 300,
  });

  // ---- QR IMAGE ----
  doc.addImage(qrDataUrl, 'PNG', 5, 10, 80, 80);

  let x = 95;
  let y = 20;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(String(lot_name), x, y);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(16);

  y += 10;
  drawLabelValue(doc, 'Estate:', estate_name, x, y);

  y += 10;
  drawLabelValue(doc, 'Harvest Date:', formatDate(harvest_date), x, y);

  y += 10;
  drawLabelValue(doc, 'Drying start:', formatDate(drying_start_date), x, y);

  // Active beds
  y += 10;

  const label = `Active beds (${active_beds.length - 1} beds):`;
  const value = active_beds.filter(b => b !== bed_label).join(', ');

  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);

  const labelWidth = doc.getTextWidth(label);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const rightMargin = 10;
  const lineHeight = 6;

  // Width available AFTER label
  const firstLineWidth = pageWidth - (x + labelWidth + 2) - rightMargin;

  // Width for lines below (full width from x)
  const nextLineWidth = pageWidth - x - rightMargin;

  // Split text
  doc.setFont('helvetica', 'normal');

  const firstLine = doc.splitTextToSize(value, firstLineWidth);
  const remainingText = firstLine.slice(1).join(' ');

  doc.text(firstLine[0], x + labelWidth + 2, y);

  // Wrap remaining lines properly
  const remainingLines = doc.splitTextToSize(remainingText, nextLineWidth);

  for (const line of remainingLines) {
    y += lineHeight;

    // 🔴 Page overflow check
    if (y > pageHeight - 10) {
      doc.addPage();
      y = 20;
    }

    doc.text(line, x, y);
  }

  // Footer
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(bed_label, 45, 100);

  const logoDataUrl = await LOGO_DATA_URL_PROMISE;

  if (logoDataUrl) {
    const logoDimensions = await getImageDimensions(logoDataUrl);
    const aspectRatio = logoDimensions.width / logoDimensions.height;

    const logoWidth = 45;
    const logoHeight = logoWidth / aspectRatio;
    doc.addImage(logoDataUrl, 'PNG', 155, 75, logoWidth, logoHeight);
  }
}

export async function generateAllBedPdfs(data, est_name, harvestDate, drying_st_date) {
  if (!Array.isArray(data) || data.length === 0) return;

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a5',
  });

  const activeBeds = getActiveBedsSorted(data);

  const lot_name = data[0].lotname;
  const estate_name = est_name;
  // const harvest_date = String(harvestDate ?? '');
  // const drying_start_date = String(drying_st_date ?? '');
  const harvest_date = harvestDate;
  const drying_start_date = drying_st_date;

  const activeBedsData = data.filter(b => b.drying_status === true);

  for (let i = 0; i < activeBedsData.length; i++) {
    if (i > 0) {
      doc.addPage();
    }

    const bed = activeBedsData[i];
    const bed_label = getBedLabel(bed.bed_number, bed.bed_position);

    await renderBedPage(doc, {
      lot_name,
      estate_name,
      harvest_date,
      drying_start_date,
      active_beds: activeBeds,
      bed_number: bed.bed_number,
      bed_position: bed.bed_position,
      bed_label,
    });
  }

  // ✅ Save ONCE
  doc.save(`${lot_name}-all-beds.pdf`);
}
